import React, { useState, useContext } from 'react';
import { ColumnDefinition } from '../reusable-components/GenericTable';
import { SearchResultsContainer } from '../reusable-components/SearchResultsContainer';
import { AuthContext } from '../reusable-components/AuthContext';
import { CurrentPageContext } from '../reusable-components/CurrentPageContext';
import { useNavigate } from 'react-router-dom';
import { isBlank, isNumeric } from '../utils/common-functions';
import {
  checkTokenAndRenderCode,
  isTokenValid,
} from '../utils/common-functions';
import { TextInput } from '../reusable-components/TextInput';
import { LimitRecordsSelect } from '../reusable-components/LimitRecordsSelect';
import { useCompanySearch } from '../hooks/use-company-search';
import { MainComponentProps } from '../reusable-components/RouterComponent';
import { SearchButtonGroup } from '../reusable-components/SearchButtonGroup';
import { useShowMessageToast } from '../hooks/use-show-message-toast';
import DeleteIcon from '../icons/DeleteIcon';
import { StandardToastContainer } from '../reusable-components/StandardToastContainer';

export const CompaniesPage: React.FC<MainComponentProps> = (props) => {
  const { skipTokenCheck = false } = props;
  const [companyName, setCompanyName] = useState<string>('');
  const [companyId, setCompanyId] = useState<string>('');
  const [limitRecords, setLimitRecords] = useState<number>(10);
  const authContext = useContext(AuthContext);
  const { token } = authContext;
  const currentPageContext = useContext(CurrentPageContext);
  const { setCurrentRoute } = currentPageContext;
  const navigate = useNavigate();
  useShowMessageToast();

  const {
    companies,
    queryInProgress,
    searchCompanies,
    handleDeleteCompanyPressed,
  } = useCompanySearch(token, companyName, companyId, limitRecords);

  // define column titles and their ordering on the table
  const companyFieldDefs: Record<string, ColumnDefinition> = {
    companyId: { title: 'Company Id', index: 0 },
    name: { title: 'Company Name', index: 1 },
    street: { title: 'Street', index: 2 },
    houseNumber: { title: 'Street Number', index: 3 },
    city: { title: 'City', index: 4 },
    zipCode: { title: 'Postal Code', index: 5 },
  };

  const checkSearchCondition = (): boolean => {
    return !queryInProgress && isAnyFieldFilled();
  };

  const isAnyFieldFilled = (): boolean => {
    return (
      (isBlank(companyId) && !isBlank(companyName)) ||
      (isNumeric(companyId) && isBlank(companyName))
    );
  };

  const pageJSX: JSX.Element = (
    <div className="md:container md:mx-auto">
      <StandardToastContainer />

      <h1 className="text-2xl font-bold my-4">Companies</h1>

      <div className="flex flex-row space-x-4">
        <TextInput
          label={'Company Name:'}
          value={companyName}
          placeholder="Any part of the company name here"
          custom_test_id="company-name-input"
          onChange={(e) => {
            setCompanyName(e.target.value);
            setCompanyId('');
          }}
          onEnterKeyPressed={() => checkSearchCondition() && searchCompanies()}
          width="w-1/3"
        />
        <TextInput
          label={'Company Id:'}
          value={companyId}
          placeholder="Number here"
          custom_test_id="company-id-input"
          onChange={(e) => {
            setCompanyId(e.target.value);
            setCompanyName('');
          }}
          onEnterKeyPressed={() => checkSearchCondition() && searchCompanies()}
          validation={isNumeric}
          errorMessage={'Company Id must be a number'}
          width="w-1/6"
        />
        <LimitRecordsSelect value={limitRecords} onChange={setLimitRecords} />
        <SearchButtonGroup
          queryInProgress={queryInProgress}
          onSearch={searchCompanies}
          isSearchDisabled={
            queryInProgress || !isTokenValid(token) || !isAnyFieldFilled()
          }
          addNewLink="/add-company"
          addNewTestId="add-new-company-link"
          addNewLabel="Add New Company"
        />
      </div>

      <SearchResultsContainer
        items={companies}
        queryInProgress={queryInProgress}
        itemsPerPage={10}
        columnDefs={companyFieldDefs}
        listOfActions={[
          [<DeleteIcon key="delete-icon" />, handleDeleteCompanyPressed],
        ]}
      />
    </div>
  );

  if (skipTokenCheck) return pageJSX;
  else
    return checkTokenAndRenderCode(token, pageJSX, setCurrentRoute, navigate);
};
